import type Phaser from "phaser";
import { createChunkStream, forgetChunk, layerDepth, stepChunkStream, type ChunkBudget, type ChunkProp, type ChunkRangeOut, type ChunkStream, type ChunkWork, type CompiledChunk, type SortReach, type WorldManifest } from "daedalus-core";
import { ChunkView, PropView, visibleChunks, type ChunkCells, type ChunkTexturePool, type ChunkPlacement, type ChunkRange, type TileAnimator } from "daedalus-renderer-phaser";
import type { LoadedWorld } from "../editor/world-loader.ts";
import { viewAfterScroll, type ScrolledCamera } from "./camera-view.ts";
import { chunkKey } from "./chunk-key.ts";
import type { ChunkLoading } from "./chunk-loading.ts";
import { besidePlacement, chunkPlacement } from "./chunk-placement.ts";
import { drivenByPresence, type PresenceProps } from "./presence.ts";
import { chunksCovering } from "./reveal.ts";

/**
 * One unit of a chunk's work: a tile layer to paint, or a run of a prop layer's props to place, those a roof's
 * presence drives apart from the rest.
 */
type ChunkUnit =
  | { readonly kind: "tile"; readonly placement: ChunkPlacement; readonly cells: ChunkCells }
  | { readonly kind: "props"; readonly view: PropView; readonly props: readonly ChunkProp[]; readonly from: number; readonly to: number; readonly depth: number }
  | { readonly kind: "driven"; readonly layerId: string; readonly props: readonly ChunkProp[]; readonly from: number; readonly to: number; readonly depth: number };

/**
 * Where a painter of a neighbouring map draws (ADR-147 decision 6): the neighbour's top-left corner in the owning map's
 * chunks and tiles, the owner's height and Players band, and the rows the depth sort spans beyond the owner. Its chunks
 * and props are placed in the owner's frame, its band layers mapped onto the owner's band and its ground layers under it,
 * and its rows sorted by `reachSortFraction`, so it draws beside the owner as one world.
 */
export interface BesideFrame {
  readonly cx: number;
  readonly cy: number;
  readonly x: number;
  readonly y: number;
  readonly ownerHeight: number;
  readonly ownerBand: number;
  readonly reach: SortReach;
}

/**
 * Paints the booted map's chunks round the camera and releases the rest (ADR-019's amendment of 2026-10-03): each
 * tile layer as a chunk texture in the chunk view, and each prop layer's props in one prop view per layer, those a
 * roof's presence drives handed to the presence props instead. The prop views live in the map the page's things and
 * the occluder fade share, so every view the world draws props in is listed in one place.
 *
 * A chunk's work is cut into units once, the first time the chunk is wanted: one per tile layer, then each prop
 * layer's props in runs of `propsPerUnit`. Daedalus's chunk stream does the units: the chunks the camera shows are
 * finished in the frame that wants them, and the one-chunk margin round the view is loaded, and the chunks left behind
 * unloaded, within `budgetMs` a frame. Only the chunks coming in and going out are touched, removed props' images and
 * released textures are reused (`propPool`, `texturePool`), and a frame allocates nothing unless a chunk enters.
 *
 * The camera's view is worked out from its scroll and zoom as the update set them (`camera-view.ts`), not read from
 * `worldView`, which until the frame renders is the view the frame before drew: a camera moving into a chunk still
 * waiting would otherwise draw it unpainted for a frame. The chunk textures live in the game's texture manager, which
 * a scene's shutdown does not empty, so the scene calls `clear` then.
 */
export class ChunkPainter {
  private readonly view: ChunkView;
  private stream: ChunkStream = createChunkStream();
  private readonly units = new Map<string, readonly ChunkUnit[]>();
  private readonly layers = new Set<string>();
  private readonly work: ChunkWork;
  private readonly budget: ChunkBudget;
  private readonly seen = { left: 0, top: 0, width: 0, height: 0 };
  private readonly wanted: ChunkRangeOut = { minCx: 0, minCy: 0, maxCx: 0, maxCy: 0 };
  private readonly shown: ChunkRangeOut = { minCx: 0, minCy: 0, maxCx: 0, maxCy: 0 };
  private painted: ChunkRange | null = null;
  private readonly withheld = new Set<string>();

  /**
   * @param scene The scene the chunks draw in.
   * @param world The loaded world.
   * @param map The map being played.
   * @param chunks The map's chunks by `cx,cy`.
   * @param animator The tile animator the chunk and prop views follow.
   * @param presence The presence props, handed the props a roof drives; null for a painter that hands none (a
   *   neighbour's, `beside`).
   * @param props The prop views by key, which the painter adds each prop layer's view to.
   * @param band The index of the Players band's first layer.
   * @param loading The chunk loading knobs.
   * @param now The clock the frame budget is read from, in milliseconds.
   * @param beside For a neighbouring map drawn beside the owner, where it draws; null for the owner's own map. A
   *   neighbour's painter is given its own prop views and no presence: a roof a neighbour's presence would drive is drawn
   *   as a plain prop in the state it was placed in, since nobody on the page stands under it.
   * @param textures The chunk textures the scene's painters hand on to each other (`ChunkTexturePool`), so that the
   *   painter of a map stepped onto paints into the textures of the painter it replaces; swept at every sync. Null
   *   shares none.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly world: LoadedWorld,
    private readonly map: WorldManifest["maps"][number],
    private readonly chunks: ReadonlyMap<string, CompiledChunk>,
    private readonly animator: TileAnimator,
    private readonly presence: PresenceProps | null,
    private readonly props: Map<string, PropView>,
    private readonly band: number,
    private readonly loading: ChunkLoading,
    now: () => number,
    private readonly beside: BesideFrame | null = null,
    private readonly textures: ChunkTexturePool | null = null,
  ) {
    this.view = new ChunkView(scene, world.manifest.tileSize, world.manifest.chunkSize, animator, loading.texturePool, (placement) => this.repaint(placement), textures);
    this.budget = { ms: loading.budgetMs, now };
    this.work = {
      units: (cx, cy) => this.unitsOf(cx, cy).length,
      apply: (cx, cy, unit) => this.apply(this.unitsOf(cx, cy)[unit] as ChunkUnit),
      revert: (cx, cy, unit) => this.revert(this.unitsOf(cx, cy)[unit] as ChunkUnit),
    };
  }

  /**
   * The chunk range wanted loaded, or null before the first sync: the chunks the camera shows are whole, and the
   * margin round them may still be loading (`pending`).
   *
   * @returns The range.
   */
  public get range(): ChunkRange | null {
    return this.painted;
  }

  /**
   * How many units of work are still to do before the range is whole and the chunks outside it are gone.
   *
   * @returns The count.
   */
  public get pending(): number {
    return this.stream.pending;
  }

  /**
   * Hides a prop on the canvas, or draws it again (ADR-142 decision 3.5): in every prop view the map has, and in the views
   * made later, so a prop a list names before its chunk's layer has been drawn is hidden when it is. A prop of a chunk
   * that leaves and enters again stays as it was set.
   *
   * @param id The prop's authored id.
   * @param withheld True to hide it.
   * @param fadeMs How long the change eases, in milliseconds; 0 for at once.
   */
  public withhold(id: string, withheld: boolean, fadeMs: number): void {
    if (withheld) {
      this.withheld.add(id);
    } else {
      this.withheld.delete(id);
    }
    for (const view of this.props.values()) {
      view.setWithheld(id, withheld, fadeMs);
    }
  }

  /**
   * Shakes a prop on the canvas, in the view that draws it (ADR-142's amendment of 2026-10-31).
   *
   * @param id The prop's authored id.
   * @param pixels The widest swing, in whole pixels.
   * @param durationMs How long the shake lasts.
   * @returns Whether a view shook it.
   */
  public shake(id: string, pixels: number, durationMs: number): boolean {
    for (const view of this.props.values()) {
      if (view.shake(id, pixels, durationMs)) {
        return true;
      }
    }
    return false;
  }

  /**
   * A chunk's units, cut the first time it is asked for: a tile layer each, then each prop layer's props in runs,
   * the props a roof's presence drives after the layer's others. A chunk whose data the painter does not hold has none,
   * and that empty list is not kept, so the chunk is cut once its data arrives (`arrived`).
   *
   * @param cx Chunk column.
   * @param cy Chunk row.
   * @returns The units, in the order they are applied.
   */
  private unitsOf(cx: number, cy: number): readonly ChunkUnit[] {
    const key = chunkKey(cx, cy);
    const known = this.units.get(key);
    if (known !== undefined) {
      return known;
    }
    const world = this.world;
    const chunkSize = world.manifest.chunkSize;
    const tileSize = world.manifest.tileSize;
    const appearances = world.manifest.objectAppearances ?? [];
    const run = Math.max(1, this.loading.propsPerUnit);
    const units: ChunkUnit[] = [];
    const chunk = this.chunks.get(key);
    if (chunk === undefined) {
      return units;
    }
    chunk.layers.forEach((layer, index) => {
      if (layer.kind === "tile") {
        const beside = this.beside;
        const placement = beside === null ? chunkPlacement(layer.id, index, cx, cy, layer.variant !== null, this.band, this.map.height) : besidePlacement(layer.id, index, cx + beside.cx, cy + beside.cy, layer.variant !== null, this.band, beside.ownerBand, beside.ownerHeight, beside.reach);
        units.push({ kind: "tile", placement, cells: { tiles: layer.tiles, chunkSize, tileSize, tilesets: chunk.tilesets, variant: layer.variant, manifest: world.assets } });
      }
    });
    chunk.layers.forEach((layer, index) => {
      if (layer.kind !== "prop") {
        return;
      }
      const beside = this.beside;
      let view = this.props.get(layer.id);
      if (view === undefined) {
        view = beside === null ? new PropView(this.scene, world.assets, tileSize, this.map.height, this.animator, true, this.loading.propPool) : new PropView(this.scene, world.assets, tileSize, beside.ownerHeight, this.animator, true, this.loading.propPool, beside.reach);
        for (const id of this.withheld) {
          view.setWithheld(id, true);
        }
        this.props.set(layer.id, view);
        this.layers.add(layer.id);
      }
      if (beside !== null) {
        const placed = layer.props.map((prop) => ({ ...prop, x: prop.x + beside.x, y: prop.y + beside.y }));
        const depth = besidePlacement(layer.id, index, cx, cy, false, this.band, beside.ownerBand, beside.ownerHeight, beside.reach).depth;
        for (let from = 0; from < placed.length; from += run) {
          units.push({ kind: "props", view, props: placed, from, to: Math.min(placed.length, from + run), depth });
        }
        return;
      }
      const depth = layerDepth(index, this.band);
      const driven = layer.props.filter((prop) => drivenByPresence(prop, appearances));
      const plain = driven.length === 0 ? layer.props : layer.props.filter((prop) => !drivenByPresence(prop, appearances));
      for (let from = 0; from < plain.length; from += run) {
        units.push({ kind: "props", view, props: plain, from, to: Math.min(plain.length, from + run), depth });
      }
      for (let from = 0; from < driven.length; from += run) {
        units.push({ kind: "driven", layerId: layer.id, props: driven, from, to: Math.min(driven.length, from + run), depth });
      }
    });
    this.units.set(key, units);
    return units;
  }

  /**
   * Does a unit: paints the tile layer, or places the run of props. A run that throws part way is taken out again
   * before the error goes on, so a unit is either done or not there, and a chunk that leaves with the unit undone
   * leaves nothing of it behind.
   *
   * @param unit The unit.
   * @throws Error when a cell's or a prop's frame is missing from the asset manifest.
   */
  private apply(unit: ChunkUnit): void {
    if (unit.kind === "tile") {
      this.view.paint(unit.placement, unit.cells);
      return;
    }
    try {
      if (unit.kind === "props") {
        unit.view.enter(unit.props, unit.from, unit.to, unit.depth, true);
      } else {
        this.presence?.enter(unit.layerId, unit.props, unit.from, unit.to, unit.depth);
      }
    } catch (error) {
      this.revert(unit);
      throw error;
    }
  }

  /**
   * Paints a tile layer's chunk again after a lost WebGL context came back, or for the first time when the chunk was
   * asked for while it was lost. A chunk that has left since is not painted.
   *
   * @param placement The layer and chunk the view was told.
   * @throws Error when a cell's frame is missing from the asset manifest.
   */
  private repaint(placement: ChunkPlacement): void {
    for (const unit of this.units.get(chunkKey(placement.cx, placement.cy)) ?? []) {
      if (unit.kind === "tile" && unit.placement.layerId === placement.layerId) {
        this.view.paint(unit.placement, unit.cells);
      }
    }
  }

  /**
   * Undoes a unit: releases the tile layer's texture, or removes the run of props.
   *
   * @param unit The unit.
   */
  private revert(unit: ChunkUnit): void {
    if (unit.kind === "tile") {
      this.view.release(unit.placement);
    } else if (unit.kind === "props") {
      unit.view.leave(unit.props, unit.from, unit.to);
    } else {
      this.presence?.leave(unit.layerId, unit.props, unit.from, unit.to);
    }
  }

  /**
   * Moves the loaded chunks toward the camera's: the chunks it will show this frame are whole when this returns, and
   * the margin round them and the chunks left behind are worked on within the frame's budget. Nothing happens, and
   * nothing is allocated, while the range wanted is the one loaded and no chunk in it was forgotten (`arrived`).
   *
   * @param camera The world camera, scrolled and zoomed for the frame about to be drawn.
   * @throws Error when a frame is missing from the asset manifest; the range is then not taken, and the next frame
   *   tries again.
   */
  public sync(camera: ScrolledCamera): void {
    this.textures?.sweep();
    const manifest = this.world.manifest;
    const chunkSize = manifest.chunkSize;
    const columns = Math.ceil(this.map.width / chunkSize);
    const rows = Math.ceil(this.map.height / chunkSize);
    const seen = viewAfterScroll(camera, this.seen);
    const beside = this.beside;
    if (beside !== null) {
      const pixels = manifest.tileSize * chunkSize;
      seen.left -= beside.cx * pixels;
      seen.top -= beside.cy * pixels;
    }
    const wanted = beside === null ? visibleChunks(seen, manifest.tileSize, chunkSize, columns, rows, this.wanted) : marginChunks(seen, manifest.tileSize * chunkSize, columns, rows, this.wanted);
    const previous = this.painted;
    const same = previous !== null && previous.minCx === wanted.minCx && previous.minCy === wanted.minCy && previous.maxCx === wanted.maxCx && previous.maxCy === wanted.maxCy;
    if (same && this.stream.targeted && this.stream.pending === 0) {
      return;
    }
    stepChunkStream(this.stream, wanted, chunksCovering(seen, manifest.tileSize, chunkSize, columns, rows, this.shown), this.work, this.budget);
    if (!same) {
      this.painted = { minCx: wanted.minCx, minCy: wanted.minCy, maxCx: wanted.maxCx, maxCy: wanted.maxCy };
    }
  }

  /**
   * Tells the painter a chunk's data has arrived: a chunk the stream took in before it, with nothing to do, is asked for
   * its units again at the next sync, so a chunk on screen or in the margin is painted the frame after it lands.
   *
   * @param cx Chunk column in the painter's map.
   * @param cy Chunk row.
   */
  public arrived(cx: number, cy: number): void {
    const key = chunkKey(cx, cy);
    if (this.units.get(key)?.length === 0) {
      this.units.delete(key);
    }
    forgetChunk(this.stream, cx, cy);
  }

  /**
   * Whether the painter has anything of a chunk drawn, or still to take out.
   *
   * @param cx Chunk column in the painter's map.
   * @param cy Chunk row.
   * @returns True while any of the chunk's units is applied.
   */
  public holds(cx: number, cy: number): boolean {
    const entries = this.stream.entries;
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index] as (typeof entries)[number];
      if (entry.cx === cx && entry.cy === cy && entry.done > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Forgets the units cut from a chunk whose data is let go, which holds nothing drawn (`holds`), so the chunk's data is
   * not kept alive by them and a later fetch of it is cut again.
   *
   * @param cx Chunk column in the painter's map.
   * @param cy Chunk row.
   */
  public forget(cx: number, cy: number): void {
    this.units.delete(chunkKey(cx, cy));
    forgetChunk(this.stream, cx, cy);
  }

  /**
   * Lets go of everything the painter drew, for a scene shutting down: every chunk texture, the pooled ones too,
   * which the game's texture manager would otherwise keep, and the prop views the painter made, which leave the
   * shared map. The painter is as new afterwards, and a later sync loads from nothing.
   */
  public clear(): void {
    this.view.clear();
    for (const layerId of this.layers) {
      this.props.get(layerId)?.clear();
      this.props.delete(layerId);
    }
    this.layers.clear();
    this.units.clear();
    this.stream = createChunkStream();
    this.painted = null;
  }
}

/**
 * The chunks a view needs with one chunk of margin round it, as `visibleChunks` gives them, for a map the view may lie
 * wholly beside: the range is cut to the map's grid, and empty (`maxCx` under `minCx`) when the view and its margin miss
 * the map.
 *
 * @param view The view in the map's pixels.
 * @param pixels A chunk's width in pixels.
 * @param columns Chunk columns in the map.
 * @param rows Chunk rows in the map.
 * @param out The range to fill.
 * @returns `out`.
 */
function marginChunks(view: { readonly left: number; readonly top: number; readonly width: number; readonly height: number }, pixels: number, columns: number, rows: number, out: ChunkRangeOut): ChunkRangeOut {
  out.minCx = Math.max(0, Math.floor(view.left / pixels) - 1);
  out.minCy = Math.max(0, Math.floor(view.top / pixels) - 1);
  out.maxCx = Math.min(columns - 1, Math.ceil((view.left + view.width) / pixels));
  out.maxCy = Math.min(rows - 1, Math.ceil((view.top + view.height) / pixels));
  if (out.maxCx < out.minCx || out.maxCy < out.minCy) {
    out.minCx = 0;
    out.minCy = 0;
    out.maxCx = -1;
    out.maxCy = -1;
  }
  return out;
}
